import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, Skeleton, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getActiveTabIndex,
  getBlockMetadata,
  setActiveTabIndexVersion2,
} from 'redux/PlanPageSlice';
import { getCompareScenario, getVarianceFilterChart } from 'redux/ScenarioSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';
import { getDataTypeBasedValuewithNewValue } from 'utils/FormatValues';
import FigmaTriangleDown from 'components/Icons/FigmaTriangleDown';

import {
  selectCompareDefaultTabIndex,
  selectCompareIndicatorRows,
  selectCompareSafeActiveIndex,
  selectDefaultTabIndex,
  selectIsCompareMode,
  selectIsKpiLoading,
  selectPlanIndicatorRows,
  selectSafeActiveIndex,
} from '../planPageRevampSelectors';
import {
  PLAN_KPI_GAP,
  PLAN_KPI_POSITIVE,
  PLAN_KPI_STACK_GAP,
  PLAN_KPI_WIDTH,
  PLAN_PAGE_GRADIENT_TOP,
  PLAN_REVAMP_RADIUS,
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
  accent,
  neutral,
  shadow,
  text as textToken,
} from '../planPageRevampUi';

interface KpiEntry {
  name: string;
  value: string;
  subtitle: string | null;
  compareDelta?: number | null;
}

const removeSign = (value: number) => (value < 0 ? -1 * value : value);

const toTotalValuesArray = (totalValues: unknown): any[] => {
  if (!totalValues) return [];
  if (Array.isArray(totalValues)) return totalValues;
  return Object.values(totalValues as Record<string, unknown>);
};

/**
 * Narrows an indicator total to something `formatValue` can actually take.
 *
 * `utils/FormatValues` handles a number or an array (which it reduces) and
 * nothing else — anything else truthy falls through to `.reduce` and throws
 * `totalValues.reduce is not a function`, which took the whole plan page down
 * with it. The API does return `total` as a string for some indicators, so the
 * guard belongs here rather than in that shared util, which the rest of the app
 * depends on.
 *
 * Returns null when there is no usable number, letting the caller fall back to
 * the `total_values` series the way it already does for a missing total.
 */
const toFormattableTotal = (value: unknown): number | any[] | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const toPlanKpiEntry = (row: any, index: number, metadata: any): KpiEntry => {
  const indicatorMeta = metadata?.indicators?.[index];
  const dataFormat =
    row?.data_format || row?.type || indicatorMeta?.data_format || indicatorMeta?.format;
  const formatProperties = row?.format_properties || indicatorMeta?.format_properties;
  const totalValues = toTotalValuesArray(row?.total_values);

  let formattedValue = '-';
  const total = toFormattableTotal(row?.total);
  if (total !== null) {
    formattedValue = String(getDataTypeBasedValuewithNewValue(dataFormat, total, formatProperties));
  } else if (totalValues.length > 0) {
    const yearlyValues = totalValues.filter((item: any) => String(item?.name ?? '').length === 4);
    const latestValue =
      yearlyValues.length > 0
        ? yearlyValues[yearlyValues.length - 1]
        : totalValues[totalValues.length - 1];
    const latestTotal = toFormattableTotal(latestValue?.value);
    if (latestTotal !== null) {
      formattedValue = String(
        getDataTypeBasedValuewithNewValue(dataFormat, latestTotal, formatProperties),
      );
    }
  }

  const yearlyValues = totalValues.filter((item: any) => String(item?.name ?? '').length === 4);
  const latestYear = yearlyValues.length > 0 ? yearlyValues[yearlyValues.length - 1] : null;

  return {
    name: row?.name ?? indicatorMeta?.name ?? '',
    value: formattedValue,
    subtitle: latestYear ? `FY${latestYear.name}` : null,
  };
};

const toCompareKpiEntry = (row: any, varianceFilter: boolean): KpiEntry => {
  let formattedValue = '-';
  const total = toFormattableTotal(row?.total);
  if (total !== null) {
    formattedValue = String(
      getDataTypeBasedValuewithNewValue(row.type, total, row.format_properties),
    );
  }

  const varianceValue = varianceFilter ? Number(row?.varience) : Number(row?.diff);
  let subtitle: string | null = null;
  if (!Number.isNaN(varianceValue) && varianceValue !== 0) {
    subtitle = varianceFilter
      ? `${removeSign(varianceValue).toFixed(1)}% vs compare`
      : `${removeSign(varianceValue).toFixed(1)} vs compare`;
  }

  return {
    name: row?.name ?? '',
    value: formattedValue,
    subtitle,
    compareDelta: Number.isNaN(varianceValue) ? null : varianceValue,
  };
};

/**
 * Edge scroll control, one definition for both directions.
 *
 * Built from the same parts as every other control in this header: the app's
 * own `FigmaTriangleDown` caret rotated a quarter turn — not Chakra's stock
 * chevron — an 8px radius rather than a pill, and the neutral border/hover
 * tokens.
 */
const KpiScrollButton = memo(
  ({ direction, onClick }: { direction: 'left' | 'right'; onClick: () => void }) => (
    <Flex
      as='button'
      type='button'
      aria-label={`Scroll metrics ${direction}`}
      onClick={onClick}
      alignItems='center'
      justifyContent='center'
      position='absolute'
      {...(direction === 'right' ? { right: 2 } : { left: 2 })}
      top='50%'
      transform='translateY(-50%)'
      zIndex={4}
      minW='28px'
      w='28px'
      h='28px'
      flexShrink={0}
      borderRadius={PLAN_REVAMP_RADIUS_SM}
      border='1px solid'
      borderColor={neutral.border}
      bg={neutral.surface}
      cursor='pointer'
      boxShadow={shadow.panel}
      transition='background 0.12s ease, border-color 0.12s ease'
      _hover={{ bg: neutral.surfaceHover, borderColor: neutral.borderStrong }}
      _active={{ bg: neutral.surfaceHover }}
    >
      <Box transform={`rotate(${direction === 'right' ? -90 : 90}deg)`} display='flex'>
        <FigmaTriangleDown width={10} height={6} color={textToken.secondary} />
      </Box>
    </Flex>
  ),
);

KpiScrollButton.displayName = 'KpiScrollButton';

interface KpiMetricCardProperties {
  entry: KpiEntry;
  index: number;
  isSelected: boolean;
  isCompareMode: boolean;
  primaryColor: string;
  textDark: string;
  textSecondary: string;
  successColor: string;
  dangerColor: string;
  onSelect: (index: number) => void;
}

const KpiMetricCard = memo(
  ({
    entry,
    index,
    isSelected,
    isCompareMode,
    primaryColor,
    textDark,
    textSecondary,
    successColor,
    dangerColor,
    onSelect,
  }: KpiMetricCardProperties) => {
    const handleClick = useCallback(() => {
      onSelect(index);
    }, [index, onSelect]);

    const subtitleColor = useMemo(() => {
      if (!entry.subtitle) return successColor;
      if (!isCompareMode || entry.compareDelta == null) return successColor;
      return entry.compareDelta > 0 ? successColor : dangerColor;
    }, [dangerColor, entry.compareDelta, entry.subtitle, isCompareMode, successColor]);

    return (
      /*
       * Each metric is a discrete card, not a cell in an open strip.
       *
       * The previous versions kept these as borderless columns sitting on the
       * header surface, separated only by whitespace and a hairline. At the
       * sizes real data produces — a short number under a long name — that
       * reads as loose text rather than as a control, which is why the row kept
       * coming back as the weak part of the page. Giving each one an actual
       * bounded surface does three things at once: it makes the click target
       * legible, it lets the cards be much denser (no need to pad emptiness
       * apart), and it gives selection somewhere to live that does not depend
       * on a faint background wash.
       *
       * Cards are equal height by stretch, so a metric that happens to carry a
       * sub-label no longer sets the height of the whole row.
       */
      <Box
        as='button'
        type='button'
        onClick={handleClick}
        title={entry.name}
        flexShrink={0}
        w={PLAN_KPI_WIDTH}
        minW={PLAN_KPI_WIDTH}
        maxW={PLAN_KPI_WIDTH}
        px='18px'
        py='16px'
        display='flex'
        flexDirection='column'
        alignItems='flex-start'
        justifyContent='center'
        gap={PLAN_KPI_STACK_GAP}
        textAlign='left'
        cursor='pointer'
        borderRadius={PLAN_REVAMP_RADIUS}
        border='1px solid'
        // `accent.border`, which the token file defines as exactly this — the
        // border of an element in an active state. This used `accent.solid`
        // doubled with a `0 0 0 1px` ring, and that 2px of full-strength purple
        // was both louder than any other selected control in the app and, being
        // a shadow, the thing getting clipped by the scroll container's
        // overflow along the row's top and left edges.
        borderColor={isSelected ? accent.border : neutral.border}
        bg={isSelected ? accent.subtle : neutral.surface}
        boxShadow={shadow.panel}
        transition='background 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease'
        _hover={
          isSelected ? undefined : { borderColor: neutral.borderStrong, bg: neutral.surfaceMuted }
        }
        _focus={{ outline: 'none' }}
        _focusVisible={{ boxShadow: `0 0 0 2px ${accent.border}` }}
      >
        <Text
          {...PLAN_TEXT.kpiLabel}
          color={textSecondary}
          w='100%'
          whiteSpace='nowrap'
          overflow='hidden'
          textOverflow='ellipsis'
        >
          {entry.name}
        </Text>
        <Text
          {...PLAN_TEXT.kpiValue}
          color={isSelected ? primaryColor : textDark}
          w='100%'
          whiteSpace='nowrap'
          overflow='hidden'
          textOverflow='ellipsis'
        >
          {entry.value}
        </Text>
        {(isSelected || isCompareMode) && entry.subtitle && (
          <Text
            {...PLAN_TEXT.kpiFeaturedSubtitle}
            color={subtitleColor}
            whiteSpace='nowrap'
            w='100%'
            overflow='hidden'
            textOverflow='ellipsis'
          >
            {entry.subtitle}
          </Text>
        )}
      </Box>
    );
  },
);

KpiMetricCard.displayName = 'KpiMetricCard';

interface KpiSummaryBarSkeletonProperties {
  count: number;
}

const KpiSummaryBarSkeleton = memo(({ count }: KpiSummaryBarSkeletonProperties) => (
  <Box position='relative'>
    <Flex
      alignItems='stretch'
      gap={PLAN_KPI_GAP}
      overflowX='hidden'
      pt='2px'
      pl='2px'
      ml='-2px'
      pb={4}
      maxWidth='calc(100% - 44px)'
    >
      {Array.from({ length: count }, (_, index) => {
        const isFeatured = index === 0;

        return (
          // Mirrors the real cards' metrics exactly, so the row doesn't
          // resize when the data lands. Every column is the same size now,
          // including the selected one — only its tint sets it apart.
          <Box
            key={index}
            flexShrink={0}
            w={PLAN_KPI_WIDTH}
            minW={PLAN_KPI_WIDTH}
            px='18px'
            py='16px'
            display='flex'
            flexDirection='column'
            alignItems='flex-start'
            justifyContent='center'
            gap={PLAN_KPI_STACK_GAP}
            borderRadius={PLAN_REVAMP_RADIUS}
            border='1px solid'
            borderColor={isFeatured ? accent.border : neutral.border}
            bg={isFeatured ? accent.subtle : neutral.surface}
          >
            <Skeleton
              height='14px'
              width='72px'
              borderRadius='4px'
              startColor='gray.100'
              endColor='gray.200'
            />
            <Skeleton
              height='20px'
              width='104px'
              borderRadius='4px'
              startColor='gray.100'
              endColor='gray.200'
            />
            {isFeatured && (
              <Skeleton
                height='12px'
                width='64px'
                borderRadius='4px'
                startColor='gray.100'
                endColor='gray.200'
              />
            )}
          </Box>
        );
      })}
    </Flex>
  </Box>
));

KpiSummaryBarSkeleton.displayName = 'KpiSummaryBarSkeleton';

const KpiSummaryBar = () => {
  const dispatch: AppDispatch = useDispatch();
  const isCompareMode = useSelector(selectIsCompareMode);
  const planIndicatorRows = useSelector(selectPlanIndicatorRows);
  const compareIndicatorRows = useSelector(selectCompareIndicatorRows);
  const metadata = useSelector(getBlockMetadata);
  const activeTabIndex = useSelector(getActiveTabIndex);
  const planDefaultTabIndex = useSelector(selectDefaultTabIndex);
  const compareDefaultTabIndex = useSelector(selectCompareDefaultTabIndex);
  const planSafeActiveIndex = useSelector(selectSafeActiveIndex);
  const compareSafeActiveIndex = useSelector(selectCompareSafeActiveIndex);
  const varianceFilter = useSelector(getVarianceFilterChart);
  const compareScenario = useSelector(getCompareScenario);
  const themeColors = useSelector(getPlanPageThemeColors);
  const isKpiLoading = useSelector(selectIsKpiLoading);
  const scrollReference = useRef<HTMLDivElement | null>(null);

  const skeletonCount = useMemo(() => {
    const indicatorCount = metadata?.indicators?.length ?? 0;
    return Math.min(Math.max(indicatorCount || 4, 2), 6);
  }, [metadata?.indicators?.length]);

  const indicatorRows = isCompareMode ? compareIndicatorRows : planIndicatorRows;
  const defaultTabIndex = isCompareMode ? compareDefaultTabIndex : planDefaultTabIndex;
  const selectedIndex = isCompareMode ? compareSafeActiveIndex : planSafeActiveIndex;
  const indicatorRowsLength = indicatorRows.length;

  useEffect(() => {
    if (indicatorRowsLength === 0) return;

    const isTabUnset = activeTabIndex === -1;
    const isTabOutOfRange = activeTabIndex >= indicatorRowsLength;

    if (isTabUnset || isTabOutOfRange) {
      dispatch(setActiveTabIndexVersion2(defaultTabIndex));
    }
  }, [activeTabIndex, defaultTabIndex, dispatch, indicatorRowsLength]);

  useEffect(() => {
    if (!isCompareMode || !compareScenario?.id || indicatorRowsLength === 0) return;
    dispatch(setActiveTabIndexVersion2(defaultTabIndex));
    // Reset KPI selection when the compare scenario changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareScenario?.id]);

  const kpiEntries = useMemo(
    () =>
      indicatorRows.map((row, index) =>
        isCompareMode
          ? toCompareKpiEntry(row, varianceFilter)
          : toPlanKpiEntry(row, index, metadata),
      ),
    [indicatorRows, isCompareMode, metadata, varianceFilter],
  );

  const onSelectIndicator = useCallback(
    (index: number) => {
      dispatch(setActiveTabIndexVersion2(index));
    },
    [dispatch],
  );

  /**
   * Which edges of the strip still have cards past them.
   *
   * Only a right-hand button existed, so once the user scrolled across there
   * was no way back short of a trackpad swipe — and no sign that anything had
   * been scrolled past. Both arrows now appear only on the side that actually
   * has more to show, which doubles as the cue for where you are in the row.
   */
  const [scrollEdges, setScrollEdges] = useState({ atStart: true, atEnd: true });

  const syncScrollEdges = useCallback(() => {
    const element = scrollReference.current;
    if (!element) return;
    const maxScroll = element.scrollWidth - element.clientWidth;
    const atStart = element.scrollLeft <= 1;
    // Treat "nothing overflows" as being at both ends, so neither arrow shows.
    const atEnd = maxScroll <= 1 || element.scrollLeft >= maxScroll - 1;

    // Returning the previous object when nothing changed is load-bearing, not a
    // micro-optimisation. Showing or hiding an arrow changes the row's layout,
    // which wakes the ResizeObserver, which calls this again — and a freshly
    // built object fails React's identity check every time, so the component
    // re-rendered in a loop until React gave up and blanked the page.
    setScrollEdges((previous) =>
      previous.atStart === atStart && previous.atEnd === atEnd ? previous : { atStart, atEnd },
    );
  }, []);

  const scrollByStep = useCallback((direction: 1 | -1) => {
    // One card plus its gap, so a press lands the row on a card edge rather
    // than halfway through one.
    scrollReference.current?.scrollBy({ left: direction * 196, behavior: 'smooth' });
  }, []);

  const onScrollLeft = useCallback(() => scrollByStep(-1), [scrollByStep]);
  const onScrollRight = useCallback(() => scrollByStep(1), [scrollByStep]);

  // Recomputed on resize as well as on scroll: whether the row overflows
  // depends on the viewport, so a window resize alone can add or remove the
  // arrows without any scrolling having happened.
  useEffect(() => {
    syncScrollEdges();
    const element = scrollReference.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return () => {};
    }
    const observer = new ResizeObserver(syncScrollEdges);
    observer.observe(element);
    return () => observer.disconnect();
  }, [syncScrollEdges, kpiEntries.length]);

  if (isKpiLoading) {
    return <KpiSummaryBarSkeleton count={skeletonCount} />;
  }

  if (kpiEntries.length === 0) return null;

  return (
    <Box position='relative'>
      {/* Stretch, so every card is as tall as the tallest — a metric that
          happens to have a sub-label no longer stands proud of the row. The
          bottom padding keeps the cards clear of the section divider; they are
          objects on the surface now, not tabs hanging off its edge. */}
      {/* `overflow-x: auto` clips at this box's own edges, so a card's border
          and panel shadow were being shaved off along the top and the left of
          the first card. The 2px inset gives them room; the matching negative
          margins keep the row optically in the same place. */}
      <Flex
        ref={scrollReference}
        onScroll={syncScrollEdges}
        alignItems='stretch'
        gap={PLAN_KPI_GAP}
        overflowX='auto'
        pt='2px'
        pl='2px'
        ml='-2px'
        pb={4}
        css={{ '&::-webkit-scrollbar': { display: 'none' } }}
        maxWidth={`calc(100% - 44px)`}
      >
        {kpiEntries.map((entry, index) => (
          <KpiMetricCard
            key={`${entry.name}-${index}`}
            entry={entry}
            index={index}
            isSelected={index === selectedIndex}
            isCompareMode={isCompareMode}
            primaryColor={accent.solid}
            // Figma's own values for this row (#121826 / #708090), which are
            // what `themeColors` already holds — a touch darker than the
            // generic revamp text ramp, and the design calls for them here.
            textDark={themeColors.textDark}
            textSecondary={themeColors.textSecondary}
            // #22C55E per Figma, not the app-wide success green (#008130) —
            // this sub-label is a variance indicator, not a success state.
            successColor={PLAN_KPI_POSITIVE}
            dangerColor={themeColors.danger}
            onSelect={onSelectIndicator}
          />
        ))}
      </Flex>

      {/* Scroll affordances. Each direction's button appears only while there
          are still cards past that edge — together they also show where you
          are in the row. The dissolve itself is the scroll row's own
          `mask-image` (computed above), not a painted overlay, so there is no
          color to keep in sync with whatever sits behind this row. */}
      {/* Painted, not masked — a `mask-image` fade was tried here and
          reverted: alpha-fading a bordered/tinted card to transparent reads
          as a rendering glitch, not a clean edge, especially right under the
          opaque scroll button. `PLAN_PAGE_GRADIENT_TOP` approximates the
          real page background (this row scrolls near the top of the page
          gradient, where that stop dominates) — closer than plain white,
          without the card visibly dissolving. */}
      {!scrollEdges.atStart && (
        <>
          <Box
            position='absolute'
            left={0}
            top={0}
            bottom='1px'
            w='72px'
            pointerEvents='none'
            zIndex={3}
            bgGradient={`linear(to-r, ${PLAN_PAGE_GRADIENT_TOP} 45%, transparent)`}
          />
          <KpiScrollButton direction='left' onClick={onScrollLeft} />
        </>
      )}

      {!scrollEdges.atEnd && (
        <>
          <Box
            position='absolute'
            right={0}
            top={0}
            bottom='1px'
            w='72px'
            pointerEvents='none'
            zIndex={3}
            bgGradient={`linear(to-l, ${PLAN_PAGE_GRADIENT_TOP} 45%, transparent)`}
          />
          <KpiScrollButton direction='right' onClick={onScrollRight} />
        </>
      )}
    </Box>
  );
};

export default memo(KpiSummaryBar);
